/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm, type FieldErrors } from 'react-hook-form';
import { z } from 'zod';
import { Toast } from 'sharedUi/Toast';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { TabView, TabPanel } from 'primereact/tabview';
import { Button } from 'sharedUi/Button';
import { Input } from 'sharedUi/Input';
import { TextArea } from 'sharedUi/TextArea';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useSearchParams } from 'react-router-dom';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import FormSelect from '../../components/forms/form-select';
import FormMultiSelect from '../../components/forms/form-multi-select';
import { useCanUpdate } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { pipelinesService } from '../../services/pipelines.service';
import { knowledgeBaseService } from '../../services/knowledge-base.service';
import { projectsService } from '../../services/projects.service';
import { valueSetsService } from '../../services/value-sets.service';
import { useValueSetOptions } from '../../hooks/useValueSetOptions';
import { aiModelsService } from '../../services/ai-models.service';
import { partnerConnectionsService } from '../../services/partner-connections.service';
import { extractErrorMessage } from '../../utils/error-handler';
import PipelineSourcesPanel from './pipeline-sources-panel';
import PipelineRunList from '../pipeline-runs/pipeline-run-list';
import { EmptyState, PageSkeleton, SectionLabel } from '../../components/common/panel';
import ScreenHeader from '../../components/common/screen-header';
import { PANEL, PRETTY } from '../../components/common/ui-kit';

const FILE_TYPES = ['PDF', 'DOCX', 'TXT', 'MD', 'HTML', 'CSV', 'JSON'];
/** Mirrors CHUNKING_STRATEGIES (pipeline_master_model). What each one DOES is
 *  what a user chooses between, so that is what the helper under the field says. */
const STRATEGIES = [
  { label: 'Recursive (paragraph → sentence → word)', value: 'RECURSIVE' },
  { label: 'Whole sentences', value: 'SENTENCE' },
  { label: 'Whole paragraphs', value: 'PARAGRAPH' },
  { label: 'Fixed-size windows', value: 'FIXED' },
];
const STRATEGY_HELP: Record<string, string> = {
  RECURSIVE: 'Splits at the coarsest break that fits — paragraph, then line, then sentence, then word. Overlap is a character tail, so a chunk can open mid-sentence. The general-purpose default.',
  SENTENCE: 'Packs whole sentences up to the size and never cuts one. Overlap carries whole sentences. Knows statute abbreviations (s. 304, No. 45, cl. (b)), so a section reference stays with its section.',
  PARAGRAPH: 'Keeps paragraphs whole; a paragraph larger than the size falls back to its sentences. Best for prose with real blank-line paragraphs — PDFs often have none, in which case it behaves like Sentence.',
  FIXED: 'Raw windows of exactly the size, cut anywhere. Only for text with no structure at all.',
};

const SCOPES = [
  { label: 'Within a page', value: 'PAGE' },
  { label: 'Across the whole document', value: 'DOCUMENT' },
];

/** What a document divides on is a property of the document, not of the engine:
 *  an Act breaks at "304.", a discharge summary at "DIAGNOSIS". So the author
 *  picks the document type and these values land in the fields below — visibly,
 *  so what is saved is what was seen. Every field stays editable afterwards.
 *
 *  A hand-written mirror of CHUNKING_PRESETS in
 *  `_4s_sdlc/pipeline/pipeline_master_model.py`; `test_frontend_mirrors_backend.py`
 *  fails if the two drift. `separators` is deliberately absent — it has no field
 *  on this form, so the backend fills it from the preset.
 */
const CHUNKING_PRESETS: Record<string, { label: string; chunkSize: number; chunkOverlap: number; chunkScope: string; boundaryPattern: string }> = {
  DEFAULT: {
    label: 'Default (any document)',
    chunkSize: 1000, chunkOverlap: 150, chunkScope: 'PAGE', boundaryPattern: '',
  },
  LEGAL: {
    label: 'Legal corpus (Acts, sections, clauses)',
    chunkSize: 1200, chunkOverlap: 150, chunkScope: 'DOCUMENT',
    boundaryPattern: '^\\s*(\\d+(?:\\.\\d+)*[A-Z]?)\\.?\\s',
  },
  HOSPITAL: {
    label: 'Hospital record (headed sections)',
    chunkSize: 800, chunkOverlap: 120, chunkScope: 'DOCUMENT',
    boundaryPattern: '^\\s*(\\w+ COMPLAINTS?|(?:PAST )?(?:MEDICAL |SURGICAL )?HISTORY(?: OF \\w+ \\w+)?|ALLERGIES|MEDICATIONS?|(?:\\w+ )?EXAMINATION|VITALS?|INVESTIGATIONS?|LAB\\w*(?: RESULTS?)?|IMAGING|ASSESSMENT|(?:\\w+ )?DIAGNOSIS|PROCEDURES?|HOSPITAL COURSE|TREATMENT|PLAN|DISCHARGE \\w+|FOLLOW[- ]?UP|ADVICE)\\b',
  },
};

const PRESET_OPTIONS = Object.entries(CHUNKING_PRESETS).map(([value, p]) => ({ value, label: p.label }));
const schema = z.object({
  pipelineCode: z.string().min(1, 'Pipeline code is required').max(150).trim(),
  name: z.string().min(1, 'Name is required').max(200).trim(),
  description: z.string().optional().or(z.literal('')),
  status: z.string().optional(),
  appId: z.string().optional().or(z.literal('')),
  supportedSourceTypes: z.array(z.string()).min(1, 'Pick at least one file type'),
  extractText: z.boolean(), extractTables: z.boolean(),
  normalizeWhitespace: z.boolean(), removeHeaders: z.boolean(), removeFooters: z.boolean(),
  kbRef: z.string().optional().or(z.literal('')),
  strategy: z.string(), chunkSize: z.number().min(100).max(20000), chunkOverlap: z.number().min(0),
  chunkPreset: z.string().optional().or(z.literal('')),
  chunkScope: z.string().optional().or(z.literal('')),
  boundaryPattern: z.string().max(300, 'Pattern is too long').optional().or(z.literal('')),
  preservePageNumber: z.boolean(), preserveFileName: z.boolean(), preserveSection: z.boolean(),
  modelRef: z.string().min(1, 'An embedding model is required'),
  dimensions: z.number().nullable().optional(),
  provider: z.string().min(1, 'A vector store is required'),
  connectionId: z.string().optional().or(z.literal('')),
  indexName: z.string().optional().or(z.literal('')),
}).refine((v) => v.chunkOverlap < v.chunkSize, { message: 'Overlap must be smaller than chunk size', path: ['chunkOverlap'] });
type FormData = z.infer<typeof schema>;

/** A labelled switch. The whole row is the label, so the hit area is the row
 *  rather than the 44px control at its end. */
const Switch = ({ label, hint, value, onChange }: { label: string; hint?: string; value: boolean; onChange: (v: boolean) => void }) => (
  <label className="flex cursor-pointer items-start justify-between gap-3 py-2.5">
    <span className="min-w-0">
      <span className="block text-sm text-ink">{label}</span>
      {hint && <span className="mt-0.5 block text-xs leading-relaxed text-ink-muted" style={PRETTY}>{hint}</span>}
    </span>
    <InputSwitch checked={value} onChange={(e) => onChange(!!e.value)} className="mt-0.5 flex-none" />
  </label>
);

/** Spec §12.2 — Pipeline Designer: General · Sources · Processing · Embeddings · Output · Runs. */
const PipelineForm = () => {
  const { id, isReady, navigateToView } = useSecureNavigation({ entity: 'pipeline', basePath: '/pipelines' });
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);
  const canEdit = useCanUpdate(JOB_NAMES.PIPELINE);

  const { control, handleSubmit, reset, watch, setValue, formState: { errors, isDirty } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      pipelineCode: '', name: '', description: '', status: 'ACTIVE', appId: '',
      supportedSourceTypes: ['PDF', 'DOCX', 'TXT', 'MD'],
      extractText: true, extractTables: true,
      normalizeWhitespace: true, removeHeaders: false, removeFooters: false,
      kbRef: '',
      strategy: 'RECURSIVE', chunkSize: 1000, chunkOverlap: 150,
      chunkPreset: 'DEFAULT', chunkScope: 'PAGE', boundaryPattern: '',
      preservePageNumber: true, preserveFileName: true, preserveSection: true,
      modelRef: '', dimensions: null, provider: 'mongodb_bruteforce', connectionId: '', indexName: '',
    },
    mode: 'onChange',
  });

  const { data: accessibleProjects = [] } = useQuery({ queryKey: ['projects', 'accessible'], queryFn: projectsService.getAccessible, staleTime: 5 * 60 * 1000 });
  const projectOptions = accessibleProjects.map((p) => ({ value: p._id, label: p.appCode ? `${p.name} (${p.appCode})` : p.name }));
  const projectLocked = accessibleProjects.length <= 1;
  const selectedAppId = watch('appId');
  useEffect(() => { if (!isEditMode && !selectedAppId && accessibleProjects.length) setValue('appId', accessibleProjects[0]._id); }, [isEditMode, selectedAppId, accessibleProjects, setValue]);

  // `?kb=<id>` — opened from a knowledge base's "Create a pipeline for this corpus".
  // The corpus is the one decision that pipeline was opened to make, so it arrives
  // made, in the KB's own project, rather than left at "None" on a tab nobody opens.
  const [searchParams] = useSearchParams();
  const kbFromUrl = !isEditMode ? searchParams.get('kb') : null;

  const { data: providerVS } = useQuery({ queryKey: ['value-sets', 'pipelineVectorStoreProvider'], queryFn: () => valueSetsService.getByKey('pipelineVectorStoreProvider', 'en'), staleTime: 5 * 60 * 1000 });
  const providerOptions = providerVS?.items?.length ? providerVS.items.map((i) => ({ value: i.code, label: i.label })) : [{ value: 'mongodb_bruteforce', label: 'MongoDB (in-process cosine)' }, { value: 'atlas_vector_search', label: 'MongoDB Atlas Vector Search' }];
  // Chunk strategy + scope — admin-editable via the Value Sets screen, falling
  // back to the built-in lists until the sets are seeded.
  const strategyOptions = useValueSetOptions('pipelineChunkStrategy', STRATEGIES);
  const scopeOptions = useValueSetOptions('pipelineChunkScope', SCOPES);

  /** Picking a document type WRITES its defaults into the fields below. Filling
   *  them in on the server instead would have saved a pipeline whose settings
   *  nobody had seen — and the form's own defaults (1000/150/PAGE) count as
   *  deliberate values there, so the preset's size and scope were silently
   *  discarded every time. */
  const applyPreset = (code: string) => {
    setValue('chunkPreset', code, { shouldDirty: true });
    const preset = CHUNKING_PRESETS[code];
    if (!preset) return;
    setValue('chunkSize', preset.chunkSize, { shouldDirty: true, shouldValidate: true });
    setValue('chunkOverlap', preset.chunkOverlap, { shouldDirty: true, shouldValidate: true });
    setValue('chunkScope', preset.chunkScope, { shouldDirty: true });
    setValue('boundaryPattern', preset.boundaryPattern, { shouldDirty: true, shouldValidate: true });
  };

  /** The corpora this pipeline may read. Only ACTIVE ones are offered, because
   *  _validate_kb_ref refuses an inactive one — a dropdown that offers a value the
   *  save rejects is the failure the backend's own /options endpoint exists to stop. */
  const { data: kbResp } = useQuery({
    queryKey: ['knowledge-bases', 'picker'],
    queryFn: () => knowledgeBaseService.list({ page: 1, page_size: 200, status: 'ACTIVE' }),
    staleTime: 60 * 1000,
  });
  const kbOptions = useMemo(() => ([
    { value: '', label: 'None — this pipeline owns its own sources' },
    ...((kbResp?.data || []).map((kb) => ({
      value: kb._id,
      label: `${kb.name} (${kb.documentCount ?? 0} docs)`,
    }))),
  ]), [kbResp]);
  const selectedKb = useMemo(
    () => (kbResp?.data || []).find((kb) => kb._id === watch('kbRef')),
    [kbResp, watch('kbRef')],
  );
  const appliedKbFromUrl = useRef(false);
  useEffect(() => {
    if (!kbFromUrl || appliedKbFromUrl.current) return;
    const kb = (kbResp?.data || []).find((k) => k._id === kbFromUrl);
    if (!kb) return;
    appliedKbFromUrl.current = true;
    setValue('kbRef', kb._id, { shouldDirty: true });
    if ((kb as any).appId) setValue('appId', String((kb as any).appId), { shouldDirty: true });
    if (kb.suggestedChunkingPreset) applyPreset(kb.suggestedChunkingPreset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kbFromUrl, kbResp]);

  const { data: modelsResp } = useQuery({ queryKey: ['ai-models', 'embedding'], queryFn: () => aiModelsService.list({ page: 1, page_size: 100, model_type: 'embedding' }), staleTime: 5 * 60 * 1000 });
  const modelOptions = (modelsResp?.data || []).map((m: any) => ({ value: m._id, label: `${m.displayName} · ${m.providerType}` }));

  const { data: connsResp } = useQuery({ queryKey: ['partner-connections', 'vector-store'], queryFn: () => partnerConnectionsService.getAll({ page: 1, page_size: 100, include_deleted: false }), staleTime: 5 * 60 * 1000 });
  const provider = watch('provider');
  const connectionOptions = useMemo(() => (connsResp?.data || [])
    .filter((c: any) => String(c.connectionType || '').toUpperCase() === `VECTOR_STORE_${String(provider || '').toUpperCase()}`)
    .map((c: any) => ({ value: c._id, label: c.name || c._id })), [connsResp, provider]);

  const { data: pipeline, isLoading } = useQuery({ queryKey: ['pipelines', id], queryFn: () => pipelinesService.getById(id!), enabled: isEditMode && isReady && !!id });
  useBreadcrumbTitle(isEditMode ? pipeline?.name : null);

  useEffect(() => {
    if (pipeline && isEditMode) {
      reset({
        pipelineCode: pipeline.pipelineCode, name: pipeline.name, description: pipeline.description || '',
        status: pipeline.status, appId: pipeline.appId || '',
        supportedSourceTypes: pipeline.supportedSourceTypes || [],
        extractText: pipeline.parserConfig?.extractText ?? true, extractTables: pipeline.parserConfig?.extractTables ?? true,
        normalizeWhitespace: pipeline.cleaningConfig?.normalizeWhitespace ?? true, removeHeaders: !!pipeline.cleaningConfig?.removeHeaders, removeFooters: !!pipeline.cleaningConfig?.removeFooters,
        kbRef: pipeline.kbRef || '',
        strategy: pipeline.chunkingConfig?.strategy || 'RECURSIVE', chunkSize: pipeline.chunkingConfig?.chunkSize ?? 1000, chunkOverlap: pipeline.chunkingConfig?.chunkOverlap ?? 150,
        chunkPreset: pipeline.chunkingConfig?.preset || '',
        chunkScope: pipeline.chunkingConfig?.scope || 'PAGE',
        boundaryPattern: pipeline.chunkingConfig?.boundaryPattern || '',
        preservePageNumber: pipeline.metadataConfig?.preservePageNumber ?? true, preserveFileName: pipeline.metadataConfig?.preserveFileName ?? true, preserveSection: pipeline.metadataConfig?.preserveSection ?? true,
        modelRef: pipeline.embeddingConfig?.modelRef || '', dimensions: pipeline.embeddingConfig?.dimensions ?? null,
        provider: pipeline.outputConfig?.provider || 'mongodb_bruteforce', connectionId: pipeline.outputConfig?.connectionId || '', indexName: pipeline.outputConfig?.indexName || '',
      });
    }
  }, [pipeline, isEditMode, reset]);

  const mutation = useMutation({
    mutationFn: (data: any) => (isEditMode && id ? pipelinesService.update(id, data) : pipelinesService.create(data)),
    onSuccess: (saved: any) => {
      queryClient.invalidateQueries({ queryKey: ['pipelines'] });
      toast.current?.show({ severity: 'success', summary: 'Saved', detail: `Pipeline ${isEditMode ? 'updated' : 'created'}`, life: 3000 });
      // Land on the pipeline itself, on Sources. Returning to the LIST left a new
      // pipeline with no next step: its Sources tab is locked until it exists,
      // so "create" looked like the only thing the screen could do.
      const savedId = saved?._id || id;
      if (savedId) setTimeout(() => navigateToView(savedId, '?tab=sources'), 800);
    },
    onError: (error: any) => toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(error, 'Failed to save the pipeline'), life: 10000 }),
  });

  const onSubmit = (d: FormData) => {
    const payload: any = {
      pipelineCode: d.pipelineCode, name: d.name, description: d.description || null, status: d.status,
      supportedSourceTypes: d.supportedSourceTypes,
      parserConfig: { extractText: d.extractText, extractTables: d.extractTables },
      cleaningConfig: { normalizeWhitespace: d.normalizeWhitespace, removeHeaders: d.removeHeaders, removeFooters: d.removeFooters },
      // Always sent, empty included: the server reads an absent key as "leave it
      // alone", so clearing the knowledge base has to be an explicit null.
      kbRef: d.kbRef || null,
      chunkingConfig: {
        strategy: d.strategy, chunkSize: d.chunkSize, chunkOverlap: d.chunkOverlap,
        // Sent only when set, so a pipeline that names none keeps exactly the
        // behaviour it had before these fields existed.
        scope: d.chunkScope || 'PAGE',
        // Always sent, empty included: the server fills an ABSENT key from the
        // preset, so omitting a cleared field would hand the pattern back.
        boundaryPattern: d.boundaryPattern || '',
        ...(d.chunkPreset ? { preset: d.chunkPreset } : {}),
      },
      metadataConfig: { preservePageNumber: d.preservePageNumber, preserveFileName: d.preserveFileName, preserveSection: d.preserveSection },
      embeddingConfig: { modelRef: d.modelRef, dimensions: d.dimensions || null },
      outputConfig: { type: 'VECTOR_INDEX', provider: d.provider, connectionId: d.connectionId || null, indexName: d.indexName || null },
    };
    if (!isEditMode && d.appId) payload.appId = d.appId;
    mutation.mutate(payload);
  };

  /** Which tab each validated field lives on, so an invalid submit can open the
   *  tab holding the problem. Fields not listed are on General. */
  const FIELD_TAB: Partial<Record<keyof FormData, number>> = {
    kbRef: 2, strategy: 2, chunkSize: 2, chunkOverlap: 2, chunkPreset: 2, chunkScope: 2, boundaryPattern: 2,
    modelRef: 3, dimensions: 3,
    provider: 4, connectionId: 4, indexName: 4,
  };
  const [tab, setTab] = useState(0);

  /** Submitting from the General tab used to do nothing at all when a required
   *  field on a hidden tab was empty: react-hook-form rendered the message on an
   *  input the user could not see. Say what is wrong and go to it. */
  const onInvalid = (formErrors: FieldErrors<FormData>) => {
    const first = Object.keys(formErrors)[0] as keyof FormData | undefined;
    if (!first) return;
    setTab(FIELD_TAB[first] ?? 0);
    toast.current?.show({
      severity: 'warn',
      summary: 'Check the highlighted field',
      detail: (formErrors[first]?.message as string) || 'A required field is missing.',
      life: 6000,
    });
  };

  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({ when: isDirty });

  if (isEditMode && isLoading) return <PageSkeleton panels={1} />;

  const B = (name: keyof FormData, label: string, hint?: string) => (
    <Controller name={name} control={control} render={({ field }) => <Switch label={label} hint={hint} value={!!field.value} onChange={field.onChange} />} />
  );
  const card = `${PANEL} ui-rise p-4`;

  return (
    <div className="p-4" data-testid="SCR-Pipeline-Form">
      <Toast ref={toast} position="top-right" />
      <ScreenHeader
        icon="pi pi-sitemap"
        onBack={() => handleNavigate('/pipelines')}
        title={isEditMode ? 'Edit Pipeline' : 'Create Pipeline'}
        right={isEditMode && pipeline && (
          <>
            <span className="font-mono text-xs text-ink-muted">{pipeline.pipelineCode}</span>
            {pipeline.version != null && (
              <span className="text-xs tabular-nums text-ink-muted">recipe v{pipeline.version}</span>
            )}
          </>
        )}
      />

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="flex flex-col gap-4 w-full">
        <TabView activeIndex={tab} onTabChange={(e) => setTab(e.index)}>
          <TabPanel header="General">
            <div className={`${card} grid grid-cols-1 sm:grid-cols-2 gap-3`}>
              <Controller name="pipelineCode" control={control} render={({ field }) => <Input label="Pipeline Code *" {...field} error={errors.pipelineCode?.message} placeholder="PIPELINE.PoliceDocuments" disabled={isEditMode} />} />
              <Controller name="name" control={control} render={({ field }) => <Input label="Name *" {...field} error={errors.name?.message} />} />
              <Controller name="status" control={control} render={({ field }) => <FormSelect label="Status" name={field.name} value={field.value || 'ACTIVE'} options={[{ label: 'Active', value: 'ACTIVE' }, { label: 'Inactive', value: 'INACTIVE' }]} onChange={field.onChange} />} />
              {!isEditMode && <Controller name="appId" control={control} render={({ field }) => <FormSelect label="Project" name={field.name} value={field.value || ''} options={projectOptions} onChange={field.onChange} disabled={projectLocked} />} />}
              <Controller name="supportedSourceTypes" control={control} render={({ field }) => (
                <div className="sm:col-span-2">
                  <FormMultiSelect label="Supported file types *" name={field.name} value={field.value || []} options={FILE_TYPES.map((t) => ({ label: t, value: t }))} onChange={field.onChange} error={!!errors.supportedSourceTypes} helperText={errors.supportedSourceTypes?.message} />
                </div>
              )} />
              <Controller name="description" control={control} render={({ field }) => <div className="sm:col-span-2"><TextArea label="Description" {...field} rows={2} /></div>} />
              {/* Where the documents come from is the biggest decision on this form,
                  and it lived only on the Processing tab behind a "None" default —
                  which is how a pipeline meant for a KB ended up owning its files. */}
              <div className="sm:col-span-2 ui-inset flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm" data-testid="PipelineForm.CorpusSummary">
                <span className="text-gray-700 dark:text-gray-300">
                  Documents come from{' '}
                  {selectedKb
                    ? <strong>{selectedKb.name}</strong>
                    : <span className="font-medium">this pipeline's own uploads</span>}
                </span>
                <button type="button" className="text-xs text-indigo-600 hover:underline dark:text-indigo-400"
                        onClick={() => setTab(2)}>
                  {selectedKb ? 'Change' : 'Use a knowledge base instead'}
                </button>
              </div>
            </div>
          </TabPanel>

          <TabPanel header="Sources" disabled={!isEditMode}>
            {isEditMode && id ? <PipelineSourcesPanel pipelineId={id} supportedTypes={watch('supportedSourceTypes') || []} canEdit={canEdit} />
              : <EmptyState icon="pi pi-file-import" title="No pipeline to attach sources to yet"
                            hint="Save the pipeline on the General tab first. Sources attach to a saved recipe." />}
          </TabPanel>

          <TabPanel header="Processing">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={card}>
                <SectionLabel icon="pi pi-file" className="mb-2.5">Parser</SectionLabel>
                {B('extractText', 'Extract text')}
                {B('extractTables', 'Extract tables', 'DOCX tables become rows; CSV is always tabular')}
              </div>
              <div className={card}>
                <SectionLabel icon="pi pi-eraser" className="mb-2.5">Cleaning</SectionLabel>
                {B('normalizeWhitespace', 'Normalise whitespace')}
                {B('removeHeaders', 'Remove running headers', 'A first line repeated on most pages')}
                {B('removeFooters', 'Remove running footers', 'A last line repeated on most pages')}
              </div>
              <div className={card}>
                <SectionLabel icon="pi pi-database" className="mb-2.5">Source of documents</SectionLabel>
                <Controller name="kbRef" control={control} render={({ field }) => (
                  <div className="flex flex-col gap-1">
                    <FormSelect label="Knowledge base" name={field.name} value={field.value || ''}
                                options={kbOptions}
                                onChange={(e) => {
                                  field.onChange(e.target.value);
                                  // Pre-fill the chunking from the corpus's own hint — the
                                  // KB knows what kind of document it holds, the pipeline
                                  // decides what to do about it.
                                  const kb = (kbResp?.data || []).find((k) => k._id === e.target.value);
                                  if (kb?.suggestedChunkingPreset) applyPreset(kb.suggestedChunkingPreset);
                                }} />
                    <span className="text-xs leading-relaxed text-ink-muted" style={PRETTY}>
                      {field.value
                        ? 'Documents come from this knowledge base, so several pipelines can index the same corpus different ways. Saving this moves any sources already uploaded here into it.'
                        : 'This pipeline owns its own sources, uploaded on the Sources tab. Pick a knowledge base to share one corpus across pipelines.'}
                    </span>
                    {selectedKb?.suggestedChunkingPreset && (
                      <span className="ui-callout px-2.5 py-2 text-xs leading-relaxed text-gray-600 dark:text-gray-300" style={PRETTY}>
                        This corpus suggests the <strong>{selectedKb.suggestedChunkingPreset}</strong> chunking
                        preset. It is only a starting point — change anything below and your value wins.
                      </span>
                    )}
                  </div>
                )} />
              </div>

              <div className={card}>
                <SectionLabel icon="pi pi-th-large" className="mb-2.5">Chunking</SectionLabel>
                <div className="flex flex-col gap-3">
                  <Controller name="chunkPreset" control={control} render={({ field }) => (
                    <div className="flex flex-col gap-1">
                      <FormSelect label="Document type" name={field.name} value={field.value || ''} options={PRESET_OPTIONS} onChange={(e) => applyPreset(e.target.value)} />
                      <span className="text-xs text-ink-muted">Picking one fills in the four settings below. Change any of them afterwards and your value is what gets saved.</span>
                    </div>
                  )} />
                  <Controller name="strategy" control={control} render={({ field }) => (
                    <div className="flex flex-col gap-1">
                      <FormSelect label="Strategy" name={field.name} value={field.value || 'RECURSIVE'} options={strategyOptions} onChange={field.onChange} />
                      <span className="text-xs leading-relaxed text-ink-muted" style={PRETTY} data-testid="PipelineForm.StrategyHelp">
                        {STRATEGY_HELP[field.value || 'RECURSIVE']}
                      </span>
                    </div>
                  )} />
                  <div className="grid grid-cols-2 gap-3">
                    <Controller name="chunkSize" control={control} render={({ field }) => (
                      <div className="flex flex-col gap-1"><label className="text-sm font-medium text-gray-700 dark:text-gray-300">Chunk size (chars)</label>
                        <InputNumber value={field.value} onValueChange={(e) => field.onChange(e.value ?? 1000)} min={100} max={20000} useGrouping={false} className="w-full" /></div>
                    )} />
                    <Controller name="chunkOverlap" control={control} render={({ field }) => (
                      <div className="flex flex-col gap-1"><label className="text-sm font-medium text-gray-700 dark:text-gray-300">Overlap (chars)</label>
                        <InputNumber value={field.value} onValueChange={(e) => field.onChange(e.value ?? 0)} min={0} useGrouping={false} className="w-full" />
                        {errors.chunkOverlap && <span className="ui-ink ui-ink--bad text-xs">{errors.chunkOverlap.message}</span>}</div>
                    )} />
                  </div>
                  <Controller name="chunkScope" control={control} render={({ field }) => (
                    <div className="flex flex-col gap-1">
                      <FormSelect label="Chunk scope" name={field.name} value={field.value || 'PAGE'} options={scopeOptions} onChange={field.onChange} />
                      <span className="text-xs text-ink-muted">Across the whole document keeps a section together when it runs over a page break.</span>
                    </div>
                  )} />
                  <Controller name="boundaryPattern" control={control} render={({ field }) => (
                    <div className="flex flex-col gap-1">
                      <Input label="Section boundary (advanced)" {...field} placeholder={'^\\s*(\\d+)\\.\\s'} error={errors.boundaryPattern?.message} />
                      <span className="text-xs text-ink-muted">A regular expression marking where a new section starts. Its first capture group names the section, so it lands on every chunk beneath it. Leave blank unless the document type above is not enough.</span>
                    </div>
                  )} />
                </div>
              </div>
              <div className={card}>
                <SectionLabel icon="pi pi-tag" className="mb-2.5">Chunk metadata</SectionLabel>
                {B('preserveFileName', 'Keep file name', 'What a citation shows')}
                {B('preservePageNumber', 'Keep page number', 'Needed for citation grading')}
                {B('preserveSection', 'Keep section heading')}
              </div>
            </div>
          </TabPanel>

          <TabPanel header="Embeddings">
            <div className={`${card} grid grid-cols-1 sm:grid-cols-2 gap-3`}>
              <Controller name="modelRef" control={control} render={({ field }) => (
                <div className="sm:col-span-2">
                  <FormSelect label="Embedding model *" name={field.name} value={field.value || ''} options={modelOptions} onChange={field.onChange} filter error={!!errors.modelRef} helperText={errors.modelRef?.message || 'Only models registered with modelType = embedding. Credentials come from the model\'s connection.'} />
                </div>
              )} />
              <Controller name="dimensions" control={control} render={({ field }) => (
                <div className="flex flex-col gap-1"><label className="text-sm font-medium text-gray-700 dark:text-gray-300">Dimensions</label>
                  <InputNumber value={field.value ?? null} onValueChange={(e) => field.onChange(e.value ?? null)} min={1} useGrouping={false} className="w-full" placeholder="Learned from the model when blank" />
                  <span className="text-xs text-ink-muted">Set only to enforce a width; a mismatch fails the run before any chunk is written.</span></div>
              )} />
            </div>
          </TabPanel>

          <TabPanel header="Output">
            <div className={`${card} grid grid-cols-1 sm:grid-cols-2 gap-3`}>
              <Controller name="provider" control={control} render={({ field }) => <FormSelect label="Vector store *" name={field.name} value={field.value || ''} options={providerOptions} onChange={(e: any) => { field.onChange(e.target ? e.target.value : e); setValue('connectionId', ''); }} error={!!errors.provider} helperText={errors.provider?.message} />} />
              <Controller name="connectionId" control={control} render={({ field }) => (
                <FormSelect label="Connection" name={field.name} value={field.value || ''} options={connectionOptions} onChange={field.onChange} showClear
                            helperText={connectionOptions.length ? 'A partner connection of the matching VECTOR_STORE_* type' : 'None needed for the platform database; add one under Partner Connections for an external store'} />
              )} />
              <Controller name="indexName" control={control} render={({ field }) => <div className="sm:col-span-2"><Input label="Index name" {...field} placeholder="pipeline_chunk_vector (default)" /></div>} />
              <p className="text-xs leading-relaxed text-gray-500 sm:col-span-2 dark:text-gray-400" style={PRETTY}>Artifact type is VECTOR_INDEX. Each run rebuilds a complete, immutable artifact version; the previous version stays until retired.</p>
            </div>
          </TabPanel>

          <TabPanel header="Runs" disabled={!isEditMode}>
            {isEditMode && id ? <PipelineRunList embedded pipelineId={id} /> : null}
          </TabPanel>
        </TabView>

        <div className="flex justify-end gap-2">
          <span className="ui-press inline-flex"><Button type="button" label="Cancel" severity="secondary" outlined onClick={() => handleNavigate('/pipelines')} /></span>
          <span className="ui-press inline-flex">
            <Button type="submit" label={mutation.isPending ? 'Saving…' : isEditMode ? 'Update Pipeline' : 'Create Pipeline'}
                    icon={mutation.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-check'} disabled={mutation.isPending} />
          </span>
        </div>
      </form>
      <DiscardChangesDialog visible={showLeaveDialog} onStay={cancelLeave} onLeave={confirmLeave} />
    </div>
  );
};

export default PipelineForm;
